import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {
    UserPlus,
    Trash2,
    Shield,
    ShieldOff,
    Check,
    Clock,
    Hourglass,
    LogOut,
    Mail,
} from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status }) {
    const map = {
        approved: { bg: "#EAF0E9", color: "#3B824E", label: "Approved", Icon: Check },
        waitlisted: {
            bg: "#F6ECE3",
            color: "#C47A55",
            label: "Waitlisted",
            Icon: Hourglass,
        },
    };
    const v = map[status] || map.waitlisted;
    const { Icon } = v;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{ background: v.bg, color: v.color }}
        >
            <Icon className="w-3 h-3" /> {v.label}
        </span>
    );
}

function RolePill({ role }) {
    if (role === "admin") {
        return (
            <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ background: "#1A3626", color: "#F9F9F6" }}
            >
                <Shield className="w-3 h-3" /> Admin
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#EAF0E9] text-[#4A5A4E]">
            User
        </span>
    );
}

export default function Admin() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [allow, setAllow] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = async () => {
        try {
            const [u, a] = await Promise.all([
                api.get("/admin/users"),
                api.get("/admin/allowlist"),
            ]);
            setUsers(u.data);
            setAllow(a.data);
        } catch (e) {
            if (e?.response?.status !== 403) toast.error("Could not load admin data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const approved = searchParams.get("approved");
        const err = searchParams.get("approve_error");
        if (approved) {
            toast.success(`${approved} approved via email link`);
            setSearchParams({}, { replace: true });
        } else if (err) {
            toast.error(`Approval link error: ${err}`);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    if (user && user.role !== "admin") {
        return <Navigate to="/dashboard" replace />;
    }

    const addAllow = async (e) => {
        e.preventDefault();
        if (!newEmail.trim()) return;
        setAdding(true);
        try {
            await api.post("/admin/allowlist", {
                email: newEmail.trim(),
                name: newName.trim() || newEmail.split("@")[0],
            });
            setNewEmail("");
            setNewName("");
            toast.success("Email allowlisted");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add");
        } finally {
            setAdding(false);
        }
    };

    const removeAllow = async (email) => {
        if (!window.confirm(`Remove ${email}? They will be re-waitlisted.`)) return;
        try {
            await api.delete(`/admin/allowlist/${encodeURIComponent(email)}`);
            toast.success("Removed");
            await load();
        } catch {
            toast.error("Could not remove");
        }
    };

    const setStatus = async (uid, status) => {
        try {
            await api.put(`/admin/users/${uid}/status`, { status });
            toast.success(status === "approved" ? "Approved" : "Moved to waitlist");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const setRole = async (uid, role) => {
        try {
            await api.put(`/admin/users/${uid}/role`, { role });
            toast.success(role === "admin" ? "Promoted to admin" : "Demoted to user");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const revoke = async (uid, email) => {
        if (!window.confirm(`Force log out ${email}?`)) return;
        try {
            const r = await api.post(`/admin/users/${uid}/revoke`);
            toast.success(`Revoked ${r.data.revoked} session(s)`);
        } catch {
            toast.error("Failed");
        }
    };

    return (
        <Shell>
            <div className="nl-fade-up">
                <p className="font-eyebrow mb-2">Console</p>
                <h1
                    className="font-serif-editorial text-4xl sm:text-5xl tracking-tight leading-[1.02]"
                    style={{ color: "#1A3626" }}
                >
                    Admin
                    <br />
                    <em className="italic font-normal">controls.</em>
                </h1>

                <Tabs defaultValue="users" className="mt-8">
                    <TabsList className="bg-[#EAF0E9] rounded-full p-1 h-auto">
                        <TabsTrigger
                            value="users"
                            data-testid="admin-tab-users"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1A3626] text-[#4A5A4E]"
                        >
                            Users ({users.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="allowlist"
                            data-testid="admin-tab-allowlist"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1A3626] text-[#4A5A4E]"
                        >
                            Allowlist ({allow.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="users" className="mt-6 space-y-3">
                        {loading && (
                            <div className="nl-card text-sm text-[#8BA092]">Loading…</div>
                        )}
                        {!loading && users.length === 0 && (
                            <div className="nl-card text-sm text-[#8BA092]">No users yet.</div>
                        )}
                        {users.map((u) => (
                            <div
                                key={u.user_id}
                                className="nl-card"
                                data-testid={`admin-user-${u.user_id}`}
                            >
                                <div className="flex items-start gap-3">
                                    {u.picture ? (
                                        <img
                                            src={u.picture}
                                            alt=""
                                            className="w-11 h-11 rounded-full object-cover border border-[#1A3626]/10 shrink-0"
                                        />
                                    ) : (
                                        <div className="w-11 h-11 rounded-full bg-[#EAF0E9] flex items-center justify-center text-[#1A3626] shrink-0">
                                            {u.name?.[0]?.toUpperCase() ?? "?"}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-medium text-[#1A3626] truncate">
                                                {u.name}
                                            </p>
                                            <RolePill role={u.role} />
                                            <StatusPill status={u.status} />
                                        </div>
                                        <p className="text-sm text-[#4A5A4E] truncate">
                                            {u.email}
                                        </p>
                                        <p className="text-[11px] text-[#8BA092] mt-0.5 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            Last active{" "}
                                            {u.last_active_at
                                                ? new Date(u.last_active_at).toLocaleString()
                                                : "—"}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {u.status === "waitlisted" ? (
                                        <button
                                            onClick={() => setStatus(u.user_id, "approved")}
                                            className="nl-btn-primary !py-2 !px-4 text-sm"
                                            data-testid={`approve-${u.user_id}`}
                                        >
                                            <Check className="w-4 h-4" /> Approve
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setStatus(u.user_id, "waitlisted")}
                                            disabled={u.role === "admin"}
                                            className="nl-btn-secondary !py-2 !px-4 text-sm disabled:opacity-40"
                                            data-testid={`waitlist-${u.user_id}`}
                                        >
                                            <Hourglass className="w-4 h-4" /> Waitlist
                                        </button>
                                    )}
                                    {u.role === "admin" ? (
                                        <button
                                            onClick={() => setRole(u.user_id, "user")}
                                            className="nl-btn-secondary !py-2 !px-4 text-sm"
                                            data-testid={`demote-${u.user_id}`}
                                        >
                                            <ShieldOff className="w-4 h-4" /> Demote
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setRole(u.user_id, "admin")}
                                            className="nl-btn-secondary !py-2 !px-4 text-sm"
                                            data-testid={`promote-${u.user_id}`}
                                        >
                                            <Shield className="w-4 h-4" /> Make admin
                                        </button>
                                    )}
                                    <button
                                        onClick={() => revoke(u.user_id, u.email)}
                                        className="nl-btn-secondary !py-2 !px-4 text-sm !bg-[#F5E4E2] !text-[#AD4A42] hover:!bg-[#EFD0CC]"
                                        data-testid={`revoke-${u.user_id}`}
                                    >
                                        <LogOut className="w-4 h-4" /> Revoke
                                    </button>
                                </div>
                            </div>
                        ))}
                    </TabsContent>

                    <TabsContent value="allowlist" className="mt-6 space-y-4">
                        <form
                            onSubmit={addAllow}
                            className="nl-card space-y-3"
                            data-testid="allowlist-form"
                        >
                            <p className="font-eyebrow">Add allowed email</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input
                                    type="email"
                                    placeholder="name@gmail.com"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    className="nl-input"
                                    data-testid="allowlist-email-input"
                                    required
                                />
                                <input
                                    type="text"
                                    placeholder="Display name"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="nl-input"
                                    data-testid="allowlist-name-input"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={adding || !newEmail.trim()}
                                className="nl-btn-primary"
                                data-testid="allowlist-add-btn"
                            >
                                <UserPlus className="w-4 h-4" />
                                {adding ? "Adding…" : "Add to allowlist"}
                            </button>
                        </form>

                        <div className="space-y-2">
                            {allow.length === 0 && (
                                <div className="nl-card text-sm text-[#8BA092]">
                                    No emails on the allowlist yet. Anyone you add here will be
                                    auto-approved on their next sign-in.
                                </div>
                            )}
                            {allow.map((a) => (
                                <div
                                    key={a.email}
                                    className="nl-card !p-4 flex items-center gap-3"
                                    data-testid={`allowlist-row-${a.email}`}
                                >
                                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-[#EAF0E9] shrink-0">
                                        <Mail className="w-5 h-5" style={{ color: "#3B824E" }} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-[#1A3626] truncate">
                                            {a.name}
                                        </p>
                                        <p className="text-sm text-[#4A5A4E] truncate">
                                            {a.email}
                                        </p>
                                        <p className="text-[11px] text-[#8BA092]">
                                            Added{" "}
                                            {a.added_at
                                                ? new Date(a.added_at).toLocaleString()
                                                : ""}
                                            {a.added_by ? ` by ${a.added_by}` : ""}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => removeAllow(a.email)}
                                        className="text-[#AD4A42] p-2 hover:bg-[#F5E4E2] rounded-full"
                                        data-testid={`allowlist-remove-${a.email}`}
                                        aria-label={`Remove ${a.email}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Shell>
    );
}
